import { useMemo, useState } from "react";
import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API, formatApiError } from "@/contexts/AuthContext";
import { EMOTIONS, EMOTION_MAP } from "@/constants/emotions";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { format, parseISO } from "date-fns";

function useMoods() {
  return useQuery({
    queryKey: ["moods"],
    queryFn: async () => (await axios.get(`${API}/moods`)).data,
  });
}

export default function Moodscape() {
  const { data: moods, isLoading } = useMoods();
  const queryClient = useQueryClient();

  const [emotion, setEmotion] = useState(null);
  const [intensity, setIntensity] = useState([3]);
  const [note, setNote] = useState("");

  const logMood = useMutation({
    mutationFn: async () =>
      (await axios.post(`${API}/moods`, { emotion, intensity: intensity[0], note })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["moods"] });
      queryClient.invalidateQueries({ queryKey: ["insights"] });
      setEmotion(null);
      setIntensity([3]);
      setNote("");
      toast.success("Mood logged.");
    },
    onError: (e) => toast.error(formatApiError(e?.response?.data?.detail) || e.message),
  });

  const deleteMood = useMutation({
    mutationFn: async (id) => axios.delete(`${API}/moods/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["moods"] });
      queryClient.invalidateQueries({ queryKey: ["insights"] });
    },
  });

  const chartData = useMemo(() => {
    if (!moods) return [];
    return [...moods]
      .reverse()
      .map((m) => ({
        date: format(parseISO(m.logged_at), "MMM d"),
        intensity: m.intensity,
        emotion: m.emotion,
      }));
  }, [moods]);

  return (
    <main className="max-w-5xl mx-auto px-6 sm:px-12 py-12 sm:py-16">
      <h1 className="font-serif-display text-4xl text-[var(--primary)] mb-2">Moodscape</h1>
      <p className="text-[var(--text-muted)] mb-10">Log how you feel, and watch the shape of it emerge.</p>

      <div className="bg-white rounded-[1.5rem] border border-[var(--border)] p-6 sm:p-8 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.08)] mb-10">
        <h2 className="text-lg font-medium mb-4">How are you feeling right now?</h2>
        <div className="flex flex-wrap gap-3 mb-6">
          {EMOTIONS.map((e) => (
            <button
              key={e.key}
              onClick={() => setEmotion(e.key)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                emotion === e.key
                  ? "text-white border-transparent"
                  : "bg-white border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--primary)]"
              }`}
              style={emotion === e.key ? { backgroundColor: e.color } : undefined}
            >
              <span>{e.emoji}</span>
              {e.label}
            </button>
          ))}
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Intensity</span>
            <span className="text-sm text-[var(--text-muted)]">{intensity[0]} / 5</span>
          </div>
          <Slider value={intensity} onValueChange={setIntensity} min={1} max={5} step={1} />
        </div>

        <Textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anything on your mind? (optional)"
          className="mb-6 rounded-2xl"
          maxLength={500}
        />

        <Button
          disabled={!emotion || logMood.isPending}
          onClick={() => logMood.mutate()}
          className="rounded-full bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white px-8"
        >
          {logMood.isPending ? "Logging…" : "Log mood"}
        </Button>
      </div>

      {!isLoading && chartData.length > 1 && (
        <div className="bg-white rounded-[1.5rem] border border-[var(--border)] p-6 sm:p-8 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.08)] mb-10">
          <h2 className="text-lg font-medium mb-4">Your Moodscape over time</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis domain={[1, 5]} tick={{ fontSize: 12 }} allowDecimals={false} />
                <Tooltip
                  formatter={(value, _name, props) => [`${value}/5`, EMOTION_MAP[props.payload.emotion]?.label]}
                />
                <Line type="monotone" dataKey="intensity" stroke="var(--primary)" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-white rounded-[1.5rem] border border-[var(--border)] p-6 sm:p-8 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.08)]">
        <h2 className="text-lg font-medium mb-4">Recent entries</h2>
        {isLoading && <p className="text-sm text-[var(--text-muted)]">Loading…</p>}
        {!isLoading && (!moods || moods.length === 0) && (
          <p className="text-sm text-[var(--text-muted)]">No entries yet — log your first mood above.</p>
        )}
        <ul className="space-y-3">
          {moods?.slice(0, 15).map((m) => {
            const meta = EMOTION_MAP[m.emotion];
            return (
              <li
                key={m.id}
                className="flex items-start gap-3 py-3 border-b border-[var(--border)] last:border-0"
              >
                <span className="text-xl leading-none mt-0.5">{meta?.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium">{meta?.label}</span>
                    <span className="text-[var(--text-muted)]">· intensity {m.intensity}/5</span>
                    <span className="text-[var(--text-muted)]">
                      · {format(parseISO(m.logged_at), "MMM d, h:mm a")}
                    </span>
                  </div>
                  {m.note && <p className="text-sm text-[var(--text-muted)] mt-1">{m.note}</p>}
                </div>
                <button
                  onClick={() => deleteMood.mutate(m.id)}
                  className="text-[var(--text-muted)] hover:text-red-500 transition-colors"
                  aria-label="Delete entry"
                >
                  <Trash2 size={16} />
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </main>
  );
}
